import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import * as Sentry from "@sentry/react";
import { createBrowserHistory } from 'history';
import { Auth0Provider } from '@auth0/auth0-react';
import './index.css';
import rootReducer from './store/reducer';

import App from './App';
import * as serviceWorker from './serviceWorker';
import thunk from 'redux-thunk';

import { StyledEngineProvider, ThemeProvider as ThemeProviderV5, createTheme as createThemeV5 } from '@mui/material/styles';
import { ThemeProvider as ThemeProviderV4, StylesProvider, createTheme as createThemeV4, createGenerateClassName } from '@material-ui/core/styles';
import { auth0Config } from './config/auth0';
import SSOChecker from './components/common/SSOChecker';
import AccountStatusGuard from './components/common/AccountStatusGuard';

// Suppress benign ResizeObserver loop error (standard CRA workaround)
// Uses addEventListener so we don't override other error handlers
window.addEventListener('error', (e) => {
  if (e instanceof ErrorEvent && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
  }
});

const themeV4 = createThemeV4({});

const themeV5 = createThemeV5({});

/**
 * Handle redirect after Auth0 login.
 * This is called after Auth0 redirects back to the app with the auth code.
 */
const onRedirectCallback = (appState?: { returnTo?: string }) => {
    // Navigate to the returnTo path or stay on current page
    // Use window.history to avoid full page reload
    const returnTo = appState?.returnTo || window.location.pathname;
    window.history.replaceState({}, document.title, returnTo);
};

const generateClassName = createGenerateClassName({
    // By enabling this option, if you have non-MUI elements (for example `<div />`)
    // using MUI classes (for example `.MuiButton`) they will lose styles.
    // Make sure to convert them to use `styled()` or `<Box />` first.
    disableGlobal: true,
    // Class names will receive this seed to avoid name collisions.
    seed: 'mui-jss',
});

const history = createBrowserHistory();

Sentry.init({
    dsn: 'https://940f0729f85db2dd558d8aa3b2f40f56@o4507783847411712.ingest.us.sentry.io/4507783854489600',
    integrations: [
        Sentry.replayIntegration(),
        Sentry.reactRouterV5BrowserTracingIntegration({ history }),
    ],
    // environment: '',
    release: 'Stokastic.Tools@1.0.0',

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Capture Replay for 5% of all sessions,
    // plus for 10% of sessions with an error
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 0.1,
})

const store = createStore(rootReducer, applyMiddleware(thunk));

function FallbackComponent({ error }: { error?: unknown }) {
    if (process.env.NODE_ENV === 'development' && error instanceof Error) {
        return (
            <div style={{ padding: 24, color: '#ff6b6b', backgroundColor: '#0A0A0A', minHeight: '100vh', fontFamily: 'monospace' }}>
                <h2>An error has occurred (dev details)</h2>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{error.message}</pre>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, opacity: 0.8 }}>{error.stack}</pre>
            </div>
        );
    }
    return <div>An error has occurred</div>;
  }

const myFallback = ({ error }: { error: unknown }) => <FallbackComponent error={error} />;

ReactDOM.render(
    <Auth0Provider {...auth0Config} onRedirectCallback={onRedirectCallback}>
        <StyledEngineProvider injectFirst>
            <StylesProvider generateClassName={generateClassName}>
                <ThemeProviderV4 theme={themeV4}>
                    <ThemeProviderV5 theme={themeV5}>
                        <Provider store={store}>
                            <Sentry.ErrorBoundary fallback={myFallback}>
                                <SSOChecker>
                                    <AccountStatusGuard>
                                        <App />
                                    </AccountStatusGuard>
                                </SSOChecker>
                            </Sentry.ErrorBoundary>
                        </Provider>,
                    </ThemeProviderV5>
                </ThemeProviderV4>
            </StylesProvider>
        </StyledEngineProvider>
    </Auth0Provider>,
    document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
